import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  X,
  Edit3,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Loader2,
  Lightbulb,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Use case options
const USE_CASE_OPTIONS = [
  { value: 'social_media', label: 'Social Media' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'education', label: 'Education' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'other', label: 'Other' }
];

const CONTENT_TYPE_LABELS = {
  video: 'Video',
  image: 'Image',
  music: 'Music',
  package: 'Content Package'
};

const TemplateEditModal = ({
  isOpen,
  onClose,
  template,
  onSaveSuccess,
  onDeleteSuccess
}) => {
  const { token } = useAuth();
  
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [useCase, setUseCase] = useState('social_media');
  const [templateText, setTemplateText] = useState('');
  const [variables, setVariables] = useState([]);

  // Load template data when modal opens
  useEffect(() => {
    if (isOpen && template) {
      setName(template.name || '');
      setDescription(template.description || '');
      setUseCase(template.use_case || 'social_media');
      setTemplateText(template.prompt_template || '');
      setVariables(template.variables || []);
      setShowAdvanced((template.variables || []).length > 0);
      setShowDeleteConfirm(false);
    }
  }, [isOpen, template]);

  const addVariable = () => {
    setVariables([...variables, {
      name: `field_${variables.length + 1}`,
      label: '',
      description: '',
      type: 'text',
      default: '',
      required: true
    }]);
  };

  const updateVariable = (index, field, value) => {
    const updated = [...variables];
    updated[index][field] = value;
    
    if (field === 'label' && value) {
      updated[index].name = value.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
    }
    
    setVariables(updated);
  };

  const removeVariable = (index) => {
    setVariables(variables.filter((_, i) => i !== index));
  };

  const insertVariable = (varName) => {
    setTemplateText(prev => prev + ' ' + `{${varName}}`);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    if (!templateText.trim()) {
      toast.error('Template instructions cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const formattedVariables = variables.map(v => ({
        name: v.name,
        label: v.label || v.name,
        description: v.description || '',
        type: v.type || 'text',
        default: v.default || '',
        required: v.required !== false
      }));

      await axios.put(`${API}/api/templates/${template.id}`, {
        name: name.trim(),
        description: description.trim(),
        content_type: template.content_type,
        use_case: useCase,
        prompt_template: templateText,
        variables: formattedVariables,
        settings: template.settings || {},
        tags: template.tags || []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Template updated!');
      onSaveSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update template:', error);
      if (error.response?.status === 403 || error.response?.status === 404) {
        toast.error("You don't have permission to edit this template.");
      } else {
        toast.error('Failed to update template. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Template deleted');
      onDeleteSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to delete template:', error);
      if (error.response?.status === 403 || error.response?.status === 404) {
        toast.error("You don't have permission to delete this template.");
      } else {
        toast.error('Failed to delete template. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Edit3 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Edit Template</h2>
              <p className="text-sm text-muted-foreground">{CONTENT_TYPE_LABELS[template.content_type]}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-red-400">Delete this template?</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This action cannot be undone. Your template "{template.name}" will be permanently removed.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDelete}
                      disabled={deleting}
                      className="gap-2"
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Yes, Delete
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Template Name */}
          <div className="space-y-2">
            <Label>Template Name <span className="text-red-500">*</span></Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My Product Launch Video"
              className="bg-background"
              data-testid="edit-template-name"
            />
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label>Description <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this template for?"
              className="bg-background min-h-[60px] resize-none"
            />
          </div>
          
          {/* Category */}
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={useCase} onValueChange={setUseCase}>
              <SelectTrigger className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {USE_CASE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Template Instructions */}
          <div className="space-y-2">
            <Label>Instructions <span className="text-red-500">*</span></Label>
            <Textarea
              value={templateText}
              onChange={(e) => setTemplateText(e.target.value)}
              placeholder="Describe what content should be created..."
              className="bg-background min-h-[100px]"
              data-testid="edit-template-text"
            />
            {variables.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-xs text-muted-foreground">Insert field:</span>
                {variables.map((v, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => insertVariable(v.name)}
                    className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full hover:bg-primary/20"
                  >
                    {`{${v.name}}`}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Advanced Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2 text-sm">
              <Lightbulb className="w-4 h-4 text-yellow-500" />
              <span>Reusable fields</span>
              {variables.length > 0 && (
                <span className="px-1.5 py-0.5 bg-primary/20 text-primary rounded-full text-xs">
                  {variables.length}
                </span>
              )}
            </div>
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {/* Variables */}
          {showAdvanced && (
            <div className="space-y-4 p-4 bg-muted/20 rounded-xl border border-border">
              <div className="flex items-center justify-between">
                <p className="font-medium text-sm">Reusable Fields</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addVariable}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Field
                </Button>
              </div>
              
              {variables.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No fields yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {variables.map((variable, index) => (
                    <div key={index} className="p-3 bg-background rounded-lg border border-border space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">Field {index + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVariable(index)}
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Label</Label>
                          <Input
                            value={variable.label}
                            onChange={(e) => updateVariable(index, 'label', e.target.value)}
                            placeholder="e.g., Product Name"
                            className="bg-muted/50 h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Default Value</Label>
                          <Input
                            value={variable.default}
                            onChange={(e) => updateVariable(index, 'default', e.target.value)}
                            placeholder="Optional"
                            className="bg-muted/50 h-8 text-sm"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-xs">Helper Text</Label>
                        <Input
                          value={variable.description}
                          onChange={(e) => updateVariable(index, 'description', e.target.value)}
                          placeholder="Describe what to enter"
                          className="bg-muted/50 h-8 text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving || deleting}
            className="text-red-400 hover:text-red-500 hover:bg-red-500/10"
            data-testid="delete-template-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !name.trim() || !templateText.trim()}
              className="gap-2"
              data-testid="update-template-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditModal;
